import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../global/text/text';
import { InfoPopover } from '../../../../../../global/info-popover/info-popover';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import ChevronSVG from '../../../../../../../images/svg/icons8-expand-arrow';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import { LOADING, REQUIRED_FIELD_TEXT } from '../../../../../../../constants/common-labels.constants';
import {
    HUB_REGISTRATION_APPROVAL_INFO_TEXT,
    HUB_REGISTRATION_APPROVAL_TOGGLE_TEXT,
    HUB_ONE_STEP_REGISTRATION_INFO_TEXT,
    HUB_ONE_STEP_REGISTRATION_TOGGLE_TEXT,
    HUB_REGISTRATION_BUTTON_LABEL_TEXT,
    HUB_REGISTRATION_DISPLAY_NAME_LABEL_TEXT,
    HUB_REGISTRATION_DISPLAY_NAME_PLACEHOLDER_TEXT,
    HUB_REGISTRATION_FIELDS_NOT_DISPLAYED_TEXT,
    HUB_REGISTRATION_FORM_SECTION_TEXT,
    HUB_REGISTRATION_INSTRUCTIONS_LABEL_TEXT,
    HUB_REGISTRATION_SUBMITTED_MESSAGE_LABEL_TEXT,
    HUB_REGISTRATION_TOGGLE_TEXT,
} from '../../../../../event-hubs.text';
import { HubOrderedFieldEntry, HubRegistrationSettingsVM } from './hub-registration-settings.viewmodel';
import { HubRegistrationFieldRow } from './hub-registration-field-row';
import { HubRegistrationFieldModal } from './hub-registration-field-modal';
import { HubCustomFieldRow } from './hub-custom-field-row';
import { HubCustomFieldModal } from './hub-custom-field-modal';
import './hub-registration-settings.scss';

type HubRegistrationSettingsProps = {
    vm: HubRegistrationSettingsVM;
};

export const HubRegistrationSettings: React.FC<HubRegistrationSettingsProps> = observer(({ vm }) => {
    if (vm.isLoading()) {
        return (
            <div className='hub-registration-settings__loading'>
                <FormattedMessage {...LOADING} values={{ suffix: '...' }} />
            </div>
        );
    }

    const displayedEntries = vm.getDisplayedOrderedFields();
    const hiddenEntries = vm.getHiddenOrderedFields();
    const editingFieldId = vm.getEditingField()?.defaultFieldId ?? null;
    const editingCustomFieldId = vm.getEditingCustomField()?.customFieldId ?? null;

    const renderEntry = (entry: HubOrderedFieldEntry) => entry.kind === 'default' ? (
        <HubRegistrationFieldRow
            key={`default-${entry.field.defaultFieldId}`}
            field={entry.field}
            vm={vm}
            isEditing={editingFieldId === entry.field.defaultFieldId}
        />
    ) : (
        <HubCustomFieldRow
            key={`custom-${entry.field.customFieldId}`}
            field={entry.field}
            vm={vm}
            isEditing={editingCustomFieldId != null && editingCustomFieldId === entry.field.customFieldId}
        />
    );

    return (
        <div className='hub-registration-settings'>
            <div className='hub-registration-settings__toggle-row'>
                <OEToggle
                    isSelected={vm.isRegistrationEnabled()}
                    onClick={vm.toggleRegistrationEnabled}
                    dataTestId='toggle-hub-registration-enabled'
                />
                <Text
                    message={vm.intl.formatMessage(HUB_REGISTRATION_TOGGLE_TEXT, {
                        suffix: vm.isRegistrationEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED,
                    })}
                />
            </div>

            <div className='hub-registration-settings__toggle-row'>
                <OEToggle
                    isSelected={vm.isOneStepRegistrationEnabled()}
                    onClick={vm.toggleOneStepRegistrationEnabled}
                    isDisabled={!vm.isRegistrationEnabled()}
                    dataTestId='toggle-hub-one-step-registration-enabled'
                />
                <Text
                    message={vm.intl.formatMessage(HUB_ONE_STEP_REGISTRATION_TOGGLE_TEXT, {
                        suffix: vm.isOneStepRegistrationEnabled()
                            ? EnablementStatus.ENABLED
                            : EnablementStatus.DISABLED,
                    })}
                />
                <InfoPopover className='m-left-8px'>
                    <FormattedMessage {...HUB_ONE_STEP_REGISTRATION_INFO_TEXT} />
                </InfoPopover>
            </div>

            {vm.isAccountRegistrationApprovalEnabled() && (
                <div className='hub-registration-settings__toggle-row hub-registration-settings__approval-toggle'>
                    <div className='hub-registration-settings__approval-toggle-label'>
                        <OEToggle
                            isSelected={vm.isRegistrationApprovalEnabled()}
                            onClick={vm.toggleRegistrationApproval}
                            dataTestId='toggle-hub-registration-approval'
                        />
                        <Text
                            message={vm.intl.formatMessage(HUB_REGISTRATION_APPROVAL_TOGGLE_TEXT, {
                                suffix: vm.isRegistrationApprovalEnabled()
                                    ? EnablementStatus.ENABLED
                                    : EnablementStatus.DISABLED,
                            })}
                        />
                    </div>
                    <InfoPopover>
                        <FormattedMessage {...HUB_REGISTRATION_APPROVAL_INFO_TEXT} />
                    </InfoPopover>
                </div>
            )}

            <div className='hub-registration-settings__page-copy'>
                <FormField
                    className='hub-registration-settings__field'
                    label={<FormattedMessage {...HUB_REGISTRATION_DISPLAY_NAME_LABEL_TEXT} />}
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={vm.getDisplayName()}
                        placeholder={vm.intl.formatMessage(HUB_REGISTRATION_DISPLAY_NAME_PLACEHOLDER_TEXT)}
                        onChange={event => vm.setDisplayName(event.target.value)}
                        data-test-id='hub-registration-display-name'
                    />
                    {vm.getDisplayNameError() &&
                        <span className='oe-form-field__error'>{vm.getDisplayNameError()}</span>}
                </FormField>

                <FormField
                    className='hub-registration-settings__field'
                    label={<FormattedMessage {...HUB_REGISTRATION_INSTRUCTIONS_LABEL_TEXT} />}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={vm.getInstructions()}
                        onChange={(_: unknown, editor: Editor) => vm.setInstructions(editor.getData())}
                        validationError={vm.getInstructionsError() || undefined}
                        dataTestId='hub-registration-instructions'
                    />
                </FormField>

                <FormField
                    className='hub-registration-settings__field'
                    label={<>
                        <FormattedMessage {...HUB_REGISTRATION_BUTTON_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={vm.getRegisterButtonLabel()}
                        onChange={event => vm.setRegisterButtonLabel(event.target.value)}
                        onBlur={vm.markRegisterButtonLabelTouched}
                        data-test-id='hub-registration-button-label'
                    />
                    {vm.getRegisterButtonLabelError() &&
                        <span className='oe-form-field__error'>{vm.getRegisterButtonLabelError()}</span>}
                </FormField>

                <FormField
                    className='hub-registration-settings__field'
                    label={<>
                        <FormattedMessage {...HUB_REGISTRATION_SUBMITTED_MESSAGE_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        value={vm.getSubmittedMessage()}
                        onChange={(_: unknown, editor: Editor) => vm.setSubmittedMessage(editor.getData())}
                        onBlur={vm.markSubmittedMessageTouched}
                        validationError={vm.getSubmittedMessageError() || undefined}
                        dataTestId='hub-registration-submitted-message'
                    />
                </FormField>
            </div>

            <div className='hub-registration-settings__section'>
                <div className='hub-registration-settings__section-header'>
                    <FormattedMessage {...HUB_REGISTRATION_FORM_SECTION_TEXT} />
                </div>

                <div className='hub-registration-settings__fields'>
                    {displayedEntries.map(renderEntry)}
                </div>

                <button
                    className='hub-registration-settings__add-custom-field-button'
                    onClick={vm.addNewCustomField}
                    disabled={!vm.canAddCustomField()}
                    type='button'
                >
                    <FormattedMessage
                        id='oe.hub.addCustomField'
                        description='Add Custom Field button'
                        defaultMessage='+ Add Custom Field'
                    />
                </button>

                <div className='hub-registration-settings__hidden-section'>
                    <button
                        type='button'
                        className={classnames('hub-registration-settings__hidden-header', {
                            'hub-registration-settings__hidden-header--expanded': vm.areHiddenFieldsExpanded(),
                        })}
                        onClick={vm.toggleHiddenFieldsExpanded}
                    >
                        <FormattedMessage {...HUB_REGISTRATION_FIELDS_NOT_DISPLAYED_TEXT} />
                        <span className='hub-registration-settings__hidden-chevron'>
                            <ChevronSVG />
                        </span>
                    </button>

                    {vm.areHiddenFieldsExpanded() && hiddenEntries.length > 0 && (
                        <div className='hub-registration-settings__hidden-fields'>
                            {hiddenEntries.map(renderEntry)}
                        </div>
                    )}
                </div>
            </div>

            {vm.getEditingField() && (
                <HubRegistrationFieldModal vm={vm} />
            )}

            {vm.getEditingCustomField() && (
                <HubCustomFieldModal vm={vm} />
            )}
        </div>
    );
});
